import type {ComponentType} from "react";
import type {AxiosError} from "axios";
import type {IApiResponse} from "core/data/services/http";
import {GAME_SLUG, IMAGES_URL} from "core/data/constants";

type Factory<T = unknown> = () => Promise<{
	default: ComponentType<T>;
}>;

export function retryFailLoad<T>(fn: Factory<T>, retriesLeft = 5, interval = 1000): Factory<T> {
	return () =>
		new Promise((resolve, reject) => {
			fn()
				.then(resolve)
				.catch((error: unknown) => {
					setTimeout(() => {
						if (retriesLeft === 1) {
							reject(error);
							return;
						}

						retryFailLoad(fn, retriesLeft - 1, interval)().then(resolve, reject);
					}, interval);
				});
		});
}

export const extractErrorMessage = (error: AxiosError<IApiResponse>) =>
	error.response?.data?.errors?.[0]?.message || error.message;

export const copyToClipboard = async (content: string) => {
	try {
		await navigator.clipboard.writeText(content);
		return true;
	} catch (_err) {
		return false;
	}
};

export const getSquadFlagURL = (squadID = 0) => `${IMAGES_URL}${GAME_SLUG}/squads/${squadID}.png`;

export const getUserAvatarURL = (userID: number, avatarVersion: number) =>
	`${IMAGES_URL}avatars/user/${userID}_${avatarVersion}.png`;

export const getNumberWithOrdinal = (number?: number) => {
	if (!number) return "";

	const suffix = ["th", "st", "nd", "rd"];
	const wholeNumber = number % 100;
	const resultedSuffix = suffix[(wholeNumber - 20) % 10] || suffix[wholeNumber] || suffix[0];

	return `${number}${resultedSuffix}`;
};

export * from "core/data/utils/countdown";
export * from "core/data/utils/socialShare";
